import React from 'react';
import { personalInfo, stats, githubAchievements, contributions, education } from '../data/mock';
import { Award, Building2, Key, Globe, GraduationCap } from 'lucide-react';

const AboutSection = () => {
  return (
    <section id="about" className="py-24 bg-zinc-950">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="mb-16">
          <p className="text-cyan-400 font-mono text-sm mb-2">// ABOUT ME</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white">Who I Am</h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Column - Photo & Info */}
          <div className="space-y-6">
            {/* Photo */}
            <div className="relative">
              <div className="aspect-square max-w-sm mx-auto lg:mx-0 rounded-2xl overflow-hidden border-2 border-zinc-800 hover:border-cyan-500/50 transition-colors">
                <img 
                  src={personalInfo.photo} 
                  alt={personalInfo.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-cyan-500/10 rounded-2xl -z-10" />
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-blue-500/10 rounded-xl -z-10" />
            </div>

            {/* Company Badge */}
            <div className="flex items-center gap-3 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl max-w-sm mx-auto lg:mx-0">
              <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-zinc-500 text-xs">Currently at</p>
                <p className="text-white font-medium">{personalInfo.company}</p>
              </div>
            </div>

            {/* Education */}
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl max-w-sm mx-auto lg:mx-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{education.degree}</p>
                  <p className="text-zinc-500 text-xs">{education.university} • {education.graduation}</p>
                </div>
              </div>
            </div>

            {/* GitHub Achievements */}
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl max-w-sm mx-auto lg:mx-0">
              <div className="flex items-center gap-2 mb-3">
                <Award className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-zinc-400">GitHub Achievements</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {githubAchievements.map((achievement) => (
                  <span 
                    key={achievement}
                    className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded-full"
                  >
                    {achievement}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Text & Stats */}
          <div className="space-y-6">
            <p className="text-lg text-zinc-300 leading-relaxed">
              {personalInfo.bio}
            </p>
            <p className="text-zinc-400 leading-relaxed">
              With 8+ years of professional experience at HAVELSAN since October 2017, 
              I specialize in building enterprise-grade security solutions and distributed systems. 
              My expertise spans from backend platform engineering with Java and Go to building 
              scalable cloud-native applications.
            </p>
            <p className="text-zinc-400 leading-relaxed">
              I'm passionate about open-source, security best practices, and contributing to 
              industry standards. Currently focused on Agentic AI development and advancing 
              IAM interoperability standards.
            </p>

            {/* Contributions */}
            <div className="grid gap-4 pt-4">
              {contributions.map((contrib, index) => (
                <div 
                  key={index}
                  className="p-4 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 border border-cyan-500/20 rounded-xl"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      {contrib.icon === 'Key' ? (
                        <Key className="w-5 h-5 text-cyan-400" />
                      ) : (
                        <Globe className="w-5 h-5 text-cyan-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-medium">{contrib.organization}</span>
                        <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">
                          {contrib.role}
                        </span>
                      </div>
                      <p className="text-zinc-500 text-sm mt-1">{contrib.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Tech Tags */}
            <div className="flex flex-wrap gap-2 pt-2">
              {['Java', 'Golang', 'Rust', 'OAuth2', 'OIDC', 'OPA'].map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-zinc-900 border border-zinc-800 rounded-full text-sm text-zinc-400"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:border-zinc-700 transition-colors group"
                >
                  <div className="text-3xl font-bold text-white mb-1 group-hover:text-cyan-400 transition-colors">
                    {stat.value}
                  </div>
                  <div className="text-zinc-500 text-xs uppercase tracking-wider">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
